// A place (spec §4): a name, a position, and an optional address and notes. Every place card is a pin
// on the Map; on a board it shows its address and a "Show on map" link. Edited in place, like a to-do.
import { useState } from 'react';
import { LIMITS } from '../../shared/doc.js';
import type { Card } from '../../shared/doc.js';
import { updateCard } from '../../shared/ops.js';
import type { ProjectConn } from '../doc/useProject.js';
import { href } from '../router.js';

export type Place = Extract<Card, { kind: 'place' }>;
const stop = (e: React.PointerEvent) => e.stopPropagation();

/**
 * A latitude or longitude field: written on blur (or Enter, which commits the same way), and only
 * when it's a number in range. A typo never silently moves the pin — it shows a short message
 * instead so the person knows why it didn't take.
 */
function CoordInput({ label, value, min, max, onCommit }: { label: string; value: number; min: number; max: number; onCommit: (v: number) => void }) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState('');

  const commit = () => {
    const trimmed = text.trim();
    if (trimmed === '') return;
    const v = Number(trimmed.replace(',', '.'));
    if (!Number.isFinite(v) || v < min || v > max) {
      setError(`Enter a number between ${min} and ${max}.`);
      return;
    }
    setError('');
    if (v !== value) onCommit(Math.round(v * 1e6) / 1e6);
  };

  return (
    <div className="coord-field">
      <label>
        {label}
        <input
          inputMode="decimal"
          value={focused ? text : String(value)}
          onFocus={() => {
            setText(String(value));
            setFocused(true);
            setError('');
          }}
          onChange={(e) => {
            setText(e.target.value);
            if (error) setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          onBlur={() => {
            setFocused(false);
            commit();
          }}
        />
      </label>
      {error && <p className="error small" role="alert">{error}</p>}
    </div>
  );
}

export function PlaceCard({ conn, projectId, card, editing, onClose }: { conn: ProjectConn; projectId: string; card: Place; editing: boolean; onClose?: () => void }) {
  const set = (fields: Record<string, unknown>) => updateCard(conn.doc, conn.me, card.id, fields);
  if (!editing) {
    return (
      <div className="place-card">
        <strong>📍 {card.name || 'Untitled place'}</strong>
        {card.address && <p className="muted small">{card.address}</p>}
        {card.notes && <p className="place-notes">{card.notes}</p>}
        <p className="small">
          <a
            href={href.place(projectId, card.id)}
            onPointerDown={stop}
            onClick={(e) => {
              // Stops the click from bubbling into an ancestor's onClick (e.g. the phone board
              // list's <li>, which would otherwise reopen this very card right after navigating
              // away), and closes the full card panel, if this is shown inside one, so the map
              // underneath is actually visible.
              e.stopPropagation();
              onClose?.();
            }}
          >
            Show on map
          </a>{' '}
          <span className="muted">
            {card.lat.toFixed(5)}, {card.lon.toFixed(5)}
          </span>
        </p>
      </div>
    );
  }
  return (
    <div className="place-card editing" onPointerDown={stop}>
      <label>
        Name
        <input value={card.name} placeholder="Place name" onChange={(e) => set({ name: e.target.value.slice(0, LIMITS.title) })} />
      </label>
      <label>
        Address
        <input value={card.address} onChange={(e) => set({ address: e.target.value.slice(0, LIMITS.address) })} />
      </label>
      <label>
        Notes
        <textarea rows={3} value={card.notes} onChange={(e) => set({ notes: e.target.value.slice(0, LIMITS.placeNotes) })} />
      </label>
      <div className="coords">
        <CoordInput label="Latitude" value={card.lat} min={-90} max={90} onCommit={(lat) => set({ lat })} />
        <CoordInput label="Longitude" value={card.lon} min={-180} max={180} onCommit={(lon) => set({ lon })} />
      </div>
    </div>
  );
}
